import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SERVICES } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const EMPTY = { name: "", phone: "", email: "", service: "", preferred_date: "", preferred_time: "", message: "" };
const TIMES = ["Morning", "Afternoon", "Evening"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#4A6660]">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export const Appointment = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.replace(/\D/g, "").length < 10 || !form.service) {
      toast.error("Please add your name, a 10-digit phone number and a service.");
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) { toast.error("Booking is temporarily unavailable."); return; }
    setLoading(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      const res = await axios.post(`${base}/api/appointments`, payload);
      setDone(res?.data ?? { name: form.name });
      setForm(EMPTY);
      toast.success("Request received — our team will call you to confirm.");
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your details and try again." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-24 sm:py-36 bg-[#EEF1EC]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Request a consultation</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#0B2B26]">
            Start with a <span className="italic text-[#0E4D44]">conversation.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#33504A] leading-relaxed max-w-md">
            Share a few details and our team will call you to confirm a slot. This is a request, not a confirmed booking.
          </p>
          <p className="mt-8 text-sm text-[#7A2E14] bg-[#F3E2D6] rounded-2xl p-4 max-w-md" data-testid="appointment-emergency-note">
            For medical emergencies, please call 108 or visit the nearest hospital.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div data-testid="appointment-request-form-container" className="rounded-[28px] bg-white border border-[#0E4D44]/10 p-6 sm:p-10 shadow-[0_30px_80px_-40px_rgba(11,43,38,0.35)]">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="appointment-success-card" className="py-10 text-center">
                <CheckCircle2 className="h-14 w-14 mx-auto text-[#0E4D44]" strokeWidth={1.4} />
                <h3 className="mt-6 font-display text-3xl sm:text-4xl text-[#0B2B26]">Thank you, {done?.name ?? "there"}.</h3>
                <p className="mt-3 text-[#33504A]">We have your request{done?.service ? ` for ${done.service}` : ""}. Expect a call shortly.</p>
                <p className="mt-2 font-mono text-xs text-[#4A6660]" data-testid="appointment-reference">Ref · {String(done?.id ?? "").slice(-6).toUpperCase()}</p>
                <button data-testid="appointment-new-request-btn" onClick={() => setDone(null)} className="btn-ghost mt-8">Make another request</button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" noValidate>
                <Field label="Full name *"><Input data-testid="appointment-input-full-name" value={form.name} onChange={set("name")} placeholder="Your name" className="field" /></Field>
                <Field label="Phone *"><Input data-testid="appointment-input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className="field" /></Field>
                <Field label="Email"><Input data-testid="appointment-input-email" type="email" value={form.email} onChange={set("email")} placeholder="Optional" className="field" /></Field>
                <Field label="Service *">
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger data-testid="appointment-select-service" className="field"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => <SelectItem key={s.id} value={s.title} data-testid={`appointment-service-option-${s.id}`}>{s.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Preferred date"><Input data-testid="appointment-input-date" type="date" min={new Date().toISOString().slice(0, 10)} value={form.preferred_date} onChange={set("preferred_date")} className="field" /></Field>
                <Field label="Preferred time">
                  <div className="flex gap-2">
                    {TIMES.map((t) => (
                      <button type="button" key={t} data-testid={`appointment-time-${t.toLowerCase()}`} onClick={() => set("preferred_time")(form.preferred_time === t ? "" : t)}
                        className={`flex-1 h-12 rounded-xl text-sm border transition-colors duration-300 ${form.preferred_time === t ? "bg-[#0E4D44] text-white border-[#0E4D44]" : "border-[#0E4D44]/15 text-[#22413B] hover:border-[#0E4D44]/40"}`}>{t}</button>
                    ))}
                  </div>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="What would you like help with?"><Textarea data-testid="appointment-input-message" value={form.message} onChange={set("message")} rows={4} placeholder="A short note about your concern" className="field min-h-[120px]" /></Field>
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-[#4A6660]">We only use these details to contact you about this request.</p>
                  <button data-testid="appointment-submit-button" type="submit" disabled={loading} className="btn-primary justify-center disabled:opacity-60">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send request <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
